'use client'

import type { ChartData } from 'chart.js'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import { CandlestickController, CandlestickElement } from 'chartjs-chart-financial'
import { useEffect, useRef } from 'react'
import { Line } from 'react-chartjs-2'
import type { ChartProps } from '../types'
import { baseChartOptions } from './ChartConfig'

// Register components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  CandlestickController,
  CandlestickElement
)

export function Chart({ data, type = 'area', timeUnit }: ChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<ChartJS | null>(null);

  const chartOptions = {
    ...baseChartOptions,
    scales: {
      x: {
        type: 'time',
        // time: {
        //   unit: timeUnit.unit,
        //   displayFormats: {
        //     [timeUnit.unit]: timeUnit.displayFormat
        //   },
        //   // Add min and max to remove padding
        //   min: data.datasets[0].data[0]?.x,
        //   max: data.datasets[0].data[data.datasets[0].data.length - 1]?.x
        // },
        display: true,
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          color: 'rgb(100, 116, 139)',
          maxTicksLimit: 7,
          padding: 8,
          autoSkip: true
        }
      },
      y: {
        type: 'linear',
        position: 'left',
        display: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.04)',
          drawOnChartArea: true
        },
        border: { display: false },
        ticks: {
          color: 'rgb(100, 116, 139)',
          padding: 8,
          maxTicksLimit: 5,
          callback: (value: number) => value.toLocaleString('en-IN')
        }
      },
      // Only show volume axis for area chart
      ...(type === 'area' && {
        volume: {
          type: 'linear',
          position: 'right',
          display: true,
          grid: {
            display: false
          },
          border: { display: false },
          ticks: {
            color: 'rgb(100, 116, 139)',
            padding: 8,
            maxTicksLimit: 5,
            callback: (value: number) => {
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'K';
              }
              return value;
            }
          }
        }
      })
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
        position: 'nearest',
        displayColors: false,
        callbacks: {
          title: (tooltipItems: any[]) => {
            const date = new Date(tooltipItems[0].parsed.x);
            return date.toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          },
          label: (context: any) => {
            if (type === 'candlestick') {
              const point = context.raw;
              return [
                `Open: ₹${point.o.toLocaleString('en-IN')}`,
                `High: ₹${point.h.toLocaleString('en-IN')}`,
                `Low: ₹${point.l.toLocaleString('en-IN')}`,
                `Close: ₹${point.c.toLocaleString('en-IN')}`
              ];
            }

            if (context.dataset.label === 'Volume') {
              const value = context.raw;
              if (value >= 1000000) {
                return `Volume: ${(value / 1000000).toFixed(1)}M`;
              } else if (value >= 1000) {
                return `Volume: ${(value / 1000).toFixed(1)}K`;
              }
              return `Volume: ${value}`;
            }

            return `₹${context.raw.toLocaleString('en-IN')}`;
          }
        }
      }
    }
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    // Destroy previous chart instance if it exists
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    // Create new chart instance
    if (type === 'candlestick') {
      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;

      chartRef.current = new ChartJS(ctx, {
        type: 'candlestick',
        data: {
          datasets: [{
            ...data.datasets[0],
            type: 'candlestick'
          }]
        } as any,
        options: chartOptions as any
      });
    }

    // Cleanup on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [data, type, chartOptions]);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {type === 'area' ? (
        <Line data={data as ChartData<'line'>} options={chartOptions as any} />
      ) : (
        <canvas ref={canvasRef} />
      )}
    </div>
  );
}